import { useMutation, useQueryClient } from '@tanstack/react-query'
import { IconButton, Stack, Tooltip } from '@mui/material'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { mitigationService, AtRiskRow } from '../api/mitigationService'
import { getAtRiskGuidance, hasOutstandingReprice } from '../utils/mitigationOpsUtils'
import { useToast } from './useToast'

interface AtRiskQuickActionsProps {
  row: AtRiskRow
  onOpenTimeline: (id: string) => void
  onManualPlacement: (id: string) => void
}

export default function AtRiskQuickActions({
  row,
  onOpenTimeline,
  onManualPlacement,
}: AtRiskQuickActionsProps) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const guidance = getAtRiskGuidance(row)

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['mitigation-at-risk'] })
    queryClient.invalidateQueries({ queryKey: ['mitigation-funnel'] })
  }

  const mitigateMutation = useMutation({
    mutationFn: () => mitigationService.mitigateNow(row.shipmentRequestId),
    onSuccess: () => {
      toast.success('Mitigation tick triggered')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to run mitigation'),
  })

  const applyPriceMutation = useMutation({
    mutationFn: () => mitigationService.applyPriceEscalation(row.shipmentRequestId),
    onSuccess: () => {
      toast.success('Price raise applied')
      invalidate()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to apply price escalation'),
  })

  const pending = mitigateMutation.isPending || applyPriceMutation.isPending

  return (
    <Stack direction="row" spacing={0.25}>
      {(guidance.action === 'MITIGATE_NOW' || guidance.action === 'EXTEND_AND_MITIGATE') && (
        <Tooltip title="Mitigate now">
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={pending}
              onClick={() => mitigateMutation.mutate()}
            >
              <PlayArrowIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {hasOutstandingReprice(row) && (
        <Tooltip title="Apply raise (shipper agreed by phone)">
          <span>
            <IconButton
              size="small"
              color="secondary"
              disabled={pending}
              onClick={() => applyPriceMutation.mutate()}
            >
              <TrendingUpIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {guidance.action === 'SOURCE_CARRIER' && (
        <Tooltip title="Manual placement — source carrier now">
          <span>
            <IconButton
              size="small"
              color="warning"
              disabled={pending}
              onClick={() => onManualPlacement(row.shipmentRequestId)}
            >
              <LocalShippingIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
      <Tooltip title="Open timeline">
        <span>
          <IconButton
            size="small"
            disabled={pending}
            onClick={() => onOpenTimeline(row.shipmentRequestId)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  )
}
